/**
 * PAYMENT SUCCESS PAGE
 *
 * Displays confirmation after successful payment.
 * Shows next steps in the onboarding process.
 */

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { createClient } from '@/lib/supabase';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface Organization {
  id: string;
  name: string;
  status: string;
}

export default function PaymentSuccessPage() {
  const { loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);

  const sessionId = searchParams.get('session_id');
  const orgId = searchParams.get('org_id');

  useEffect(() => {
    const fetchOrganization = async () => {
      if (!orgId) {
        setLoading(false);
        return;
      }

      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('organizations')
          .select('id, name, status')
          .eq('id', orgId)
          .single();

        if (error) throw error;
        setOrganization(data);
      } catch (err) {
        console.error('Error fetching organization:', err);
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      fetchOrganization();
    }
  }, [orgId, authLoading]);

  if (authLoading || loading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12 max-w-2xl">
      <Card className="border-green-200 dark:border-green-800">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <CheckCircle className="h-16 w-16 text-green-500" />
          </div>
          <CardTitle className="text-2xl">Payment Successful!</CardTitle>
          <CardDescription>
            Thank you for your payment. Your organization onboarding is now in progress.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {organization && (
            <div className="bg-muted p-4 rounded-lg">
              <h3 className="font-semibold mb-2">Organization Details</h3>
              <p className="text-sm text-muted-foreground">
                <strong>Name:</strong> {organization.name}
              </p>
              <p className="text-sm text-muted-foreground">
                <strong>Status:</strong> Payment Received
              </p>
            </div>
          )}

          <div className="space-y-4">
            <h3 className="font-semibold">What happens next?</h3>
            <ol className="space-y-3">
              <li className="flex gap-3">
                <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm font-medium">
                  1
                </span>
                <div>
                  <p className="font-medium">IT Team Provisioning</p>
                  <p className="text-sm text-muted-foreground">
                    Our IT team will set up the technical infrastructure for your locations.
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm font-medium">
                  2
                </span>
                <div>
                  <p className="font-medium">Data Team Configuration</p>
                  <p className="text-sm text-muted-foreground">
                    Our data team will configure your data pipeline and perform testing.
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm font-medium">
                  3
                </span>
                <div>
                  <p className="font-medium">Final Activation</p>
                  <p className="text-sm text-muted-foreground">
                    Once everything is ready, we&apos;ll activate your organization in the system.
                  </p>
                </div>
              </li>
            </ol>
          </div>

          <div className="pt-4 border-t">
            <p className="text-sm text-muted-foreground mb-4">
              You can track the progress of your organization&apos;s onboarding from the dashboard.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild className="flex-1">
                <Link to="/home">
                  Go to Dashboard
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              {organization && (
                <Button asChild variant="outline" className="flex-1">
                  <Link to={`/review/${organization.id}`}>
                    View Organization Details
                  </Link>
                </Button>
              )}
            </div>
          </div>

          {sessionId && (
            <div className="text-xs text-muted-foreground text-center pt-4">
              Payment ID: {sessionId}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
